<template>
    <v-container class="pa-4">
        <v-card class="elevation-4">
            <v-card-title>
                <span class="text">{{ title }}</span>
                <v-spacer />
                <v-btn-toggle v-model="selectedI18n">
                    <v-btn v-for="(option, i) in i18nOptions" :key="i">
                        <v-icon small>{{ i18nConfig[option] }}</v-icon>
                    </v-btn>
                </v-btn-toggle>
            </v-card-title>
            <v-card-text class="mt-4">
                <v-form ref="form" lazy-validation @submit.prevent="submit">
                    <v-row
                        class="mt-0"
                        v-for="(field, i) in computedFields"
                        :key="i"
                    >
                        <v-col cols="1" class="mt-5">
                            {{ field.name }}
                        </v-col>
                        <v-col cols="11">
                            <v-text-field
                                v-if="['text', 'password'].includes(field.type)"
                                :type="field.type"
                                v-model="form[field.key]"
                                :rules="field.rules"
                                outlined
                            ></v-text-field>
                            <number-input
                                v-if="'number' === field.type"
                                :type="field.type"
                                v-model.number="form[field.key]"
                                :rules="field.rules"
                                outlined
                                :min="field.min"
                                :max="field.max"
                                :default="field.default"
                            ></number-input>
                            <template v-if="'text-i18n' === field.type">
                                <v-text-field
                                    :type="field.type"
                                    v-model="form[field.key][currentI18nKey]"
                                    :rules="field.rules"
                                    @change="forceUpdateI18n(field)"
                                    outlined
                                ></v-text-field>
                            </template>
                            <template v-if="'editor-i18n' === field.type">
                                <tinymce-editor
                                    v-model="form[field.key][currentI18nKey]"
                                    :rules="field.rules"
                                />
                            </template>
                            <v-select
                                v-if="'select' === field.type"
                                v-model="form[field.key]"
                                :rules="field.rules"
                                :items="field.items"
                                outlined
                            ></v-select>
                            <v-combobox
                                v-if="'combobox' === field.type"
                                v-model="form[field.key]"
                                :label="field.name"
                                :rules="field.rules"
                                :items="field.items"
                                multiple
                                small-chips
                                outlined
                            ></v-combobox>
                            <v-layout
                                v-if="'checkbox' === field.type"
                                align-center
                            >
                                <span class="mr-3">{{ field.name }}</span>
                                <v-switch
                                    v-model="form[field.key]"
                                    :rules="field.rules"
                                    :items="field.items"
                                    outlined
                                ></v-switch>
                            </v-layout>
                            <v-radio-group
                                v-if="'radio' === field.type"
                                v-model="form[field.key]"
                                :rules="field.rules"
                                solo
                                row
                            >
                                <v-radio
                                    v-for="(item, j) in field.items"
                                    :key="j"
                                    :label="item.text"
                                    :value="item.value"
                                ></v-radio>
                            </v-radio-group>
                            <options-input
                                v-if="'options' === field.type"
                                v-model="form[field.key]"
                                :field="field"
                                :selectedI18n="selectedI18n"
                                :autoOptions="!editMode"
                            >
                            </options-input>
                            <v-select
                                v-if="'options-answer' === field.type"
                                v-model="form[field.key]"
                                :label="field.name"
                                :rules="field.rules"
                                :items="optionsAnswerItems"
                                outlined
                            ></v-select>
                            <date-input
                                v-if="'date' === field.type"
                                v-model="form[field.key]"
                                :field="field"
                            ></date-input>
                            <image-input
                                v-if="'image' === field.type"
                                v-model="form[field.key]"
                                :field="field"
                            >
                            </image-input>
                            <v-file-input
                                v-if="'file' === field.type"
                                v-model="form[field.key]"
                                :prepend-icon="null"
                                :accept="field.accept"
                                :rules="field.rules"
                                append-icon="mdi-file"
                                outlined
                            >
                                <template v-slot:selection="{ file }">
                                    <span v-if="file && file.name">{{
                                        file.name
                                    }}</span>
                                    <template v-else>附件</template>
                                </template>
                            </v-file-input>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="submit"
                    :loading="loading"
                    >確定</v-btn
                >
                <v-btn
                    color="grey darken-3"
                    text
                    @click="cancel"
                    :disabled="loading"
                    >取消</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
import _ from 'lodash';
import i18nMixin from '@/views/mixins/i18nMixin';
import OptionsInput from '@/components/OptionsInput.vue';
import ImageInput from '@/components/ImageInput.vue';
import TinymceEditor from '@/components/TinymceEditor.vue';
import DateInput from '@/components/DateInput.vue';
import NumberInput from '@/components/NumberInput.vue';

export default {
    mixins: [i18nMixin],
    components: {
        OptionsInput,
        ImageInput,
        TinymceEditor,
        DateInput,
        NumberInput,
    },
    data: () => ({
        //
        params: {
            name: undefined,
            editMode: false,
            resource: undefined,
        },
        //
        selectedI18n: 0,
        loading: false,
        form: {},
    }),
    computed: {
        title() {
            return (!this.editMode ? '建立' : '編輯') + _.get(this.params, 'name', '內容');
        },
        fields() {
            return _.get(this.params, 'fields', []);
        },
        computedFields() {
            return _.chain(this.fields)
                .filter((field) => !this.editMode || !field.immutable)
                .filter((field) => {
                    if (field.condition) {
                        const { ref, value } = field.condition;
                        return this.form[ref] === value;
                    }
                    return !field.condition;
                })
                .map((field) => {
                    const rules = field.rules || [];
                    if (field.required) {
                        rules.push((v) => ![null, undefined, ''].includes(v) || `請填寫${field.name}`);
                    }
                    return { ...field, rules };
                })
                .value();
        },
        optionsAnswerItems() {
            const optionsKey = _.chain(this.fields).find(['type', 'options']).get('key').value();
            return _.chain(this.form)
                .get(optionsKey)
                .map((option, i) => ({ text: option, value: i }))
                .value();
        },
    },
    methods: {
        init() {
            const fromRouter = Object.keys(this.$route.params).length !== 0;
            const params = fromRouter ? this.$route.params : JSON.parse(localStorage.getItem('form-params'));
            if (fromRouter) localStorage.setItem('form-params', JSON.stringify(params));
            this.params = { ...this.params, ...params };
            this.editMode = !!this.params.payload;

            if (this.editMode) {
                this.form = this.params.payload;
            } else {
                _.chain(this.fields).filter((field) => _.includes(field.type, 'i18n'))
                    .each((field) => {
                        this.form[field.key] = this.i18nDefaultValue();
                    }).value();
            }
        },
        forceUpdateI18n(field) {
            this.form = { ...this.form, [field.key]: this.form[field.key] };
        },
        async submit() {
            if (this.$refs.form.validate()) {
                this.loading = true;
                try {
                    if (!this.editMode) {
                        await this.$store.dispatch(`${this.params.resource}/create`, this.form);
                    } else {
                        await this.$store.dispatch(`${this.params.resource}/update`, this.form);
                    }
                    this.close();
                } catch (e) {
                    this.$showApiError(e);
                } finally {
                    this.loading = false;
                }
            }
        },
        cancel() {
            this.close();
        },
        close() {
            this.$router.go(-1);
        },
    },
    mounted() {
        this.init();
    },
};
</script>
<style>
</style>
